import jsonata from 'jsonata';
import { EventSource } from '@loyalty/shared-common';
import { IncomingWebhookType } from '@loyalty/shared-common/ingestor-api-client';
import { ManagerIncomingWebhookAttributes } from '@loyalty/shared-common/manager-api-client';

const REQUIRED_INTERACTION_JSONATA_FIELDS = ['status', 'externalId', 'label'] as const;

export interface JsonataValidationResult {
  valid: boolean;
  error?: string;
}

/** Top-level keys declared in a JSONata object literal. */
function getObjectLiteralKeys(ast: jsonata.ExprNode): string[] {
  if (ast.type !== 'unary' || ast.value !== '{' || !Array.isArray(ast.lhs)) {
    return [];
  }

  return ast.lhs.flatMap(entry => {
    const key = Array.isArray(entry) ? entry[0] : undefined;
    const keyName =
      key?.type === 'string'
        ? key.value
        : key?.type === 'path' && key.steps?.length === 1 && key.steps[0].type === 'name'
        ? key.steps[0].value
        : undefined;

    return typeof keyName === 'string' ? [keyName] : [];
  });
}

export function validateJsonata(
  rawJsonata: string,
  requiredFields: readonly string[] = [],
): JsonataValidationResult {
  try {
    const expression = jsonata(rawJsonata);
    const outputFields = getObjectLiteralKeys(expression.ast());
    const missingFields = requiredFields.filter(field => !outputFields.includes(field));

    if (missingFields.length > 0) {
      return {
        valid: false,
        error: `JSONata must include the fields: ${missingFields.join(', ')}.`,
      };
    }

    return { valid: true };
  } catch (error: unknown) {
    return {
      valid: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

export function validateInteractionJsonata(rawJsonata: string): JsonataValidationResult {
  return validateJsonata(rawJsonata, REQUIRED_INTERACTION_JSONATA_FIELDS);
}

export async function extractCountersAndTags(webhook: ManagerIncomingWebhookAttributes): Promise<{
  counters: string[];
  tags: string[];
}> {
  const rawJsonata = webhook.config?.jsonata;
  if (rawJsonata?.includes('tags') || rawJsonata?.includes('counters')) {
    try {
      const parser = jsonata(rawJsonata);
      const parsed = await parser.evaluate({});
      const tags = (parsed.payload?.tags || []).map((tag: any) => tag.name);
      const counters = (parsed.payload?.counters || []).map((counter: any) => counter.name);
      return { counters, tags };
    } catch (error) {
      console.warn('Error parsing jsonata: ', error);
      return { counters: [], tags: [] };
    }
  }
  return { counters: [], tags: [] };
}

export function filterBinaryWebhooks(
  webhooks?: ManagerIncomingWebhookAttributes[],
): ManagerIncomingWebhookAttributes[] {
  if (!webhooks) {
    return [];
  }
  return webhooks.filter(
    webhook =>
      webhook.type === IncomingWebhookType.MAPPED_EVENT &&
      webhook.config?.mapTo?.source === EventSource.BINARY,
  );
}
